import React, { useEffect, useState, useContext } from 'react';
import Router, { NextRouter } from 'next/router';
import NProgress from 'nprogress';

interface RouterProviderInterface {
  router: NextRouter;
  children: React.ReactNode;
}

interface RouterContextInterface {
  restoreScrollPos: (url: string) => void;
}

const RouterContext = React.createContext<RouterContextInterface | undefined>(
  undefined,
);

export const useRouterEffect = () => {
  const context = useContext(RouterContext);
  if (context === undefined) {
    throw new Error('useRouterEffect must be used within a RouterProvider');
  }
  return context;
};

export const RouterProvider: React.FC<RouterProviderInterface> = ({
  router,
  children,
}) => {
  const [scrollPos, setScrollPos] = useState<{
    [key: string]: { x: number; y: number };
  }>({});
  const addScrollPos = (url: string) => {
    const newScrollPos = { x: window.scrollX, y: window.scrollY };
    setScrollPos((prev) => {
      return {
        ...prev,
        [url]: newScrollPos,
      };
    });
  };
  const restoreScrollPos = (url: string) => {
    if (scrollPos[url]) {
      window.scrollTo(scrollPos[url].x, scrollPos[url].y);
    }
  };
  useEffect(() => {
    Router.events.on('routeChangeStart', () => {
      NProgress.start();
    });
    Router.events.on('routeChangeComplete', () => NProgress.done());
    Router.events.on('routeChangeError', () => NProgress.done());
  }, []);

  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      let shouldScrollRestore = false;
      window.history.scrollRestoration = 'manual';
      restoreScrollPos(router.asPath);

      const onRouteChangeStart = () => {
        addScrollPos(router.asPath);
      };
      const onRouteChangeComplete = (url: string) => {
        if (shouldScrollRestore) {
          shouldScrollRestore = false;
          restoreScrollPos(url);
        }
      };

      Router.events.on('routeChangeStart', onRouteChangeStart);
      Router.events.on('routeChangeComplete', onRouteChangeComplete);
      Router.beforePopState(() => {
        shouldScrollRestore = true;
        return true;
      });

      return () => {
        Router.events.off('routeChangeStart', onRouteChangeStart);
        Router.events.off('routeChangeComplete', onRouteChangeComplete);
        Router.beforePopState(() => true);
      };
    }
  }, [router]);

  return (
    <RouterContext.Provider value={{ restoreScrollPos }}>
      {children}
    </RouterContext.Provider>
  );
};
